import type { Align, Rect, Side } from "./types";

/** The breath between ring and target (--space-2). */
export const RING_GAP = 8;
/** The stem from the ring to the card (--space-5). */
export const STEM = 20;
/** The card's width (w-80). */
export const CARD_W = 320;
/** The window inset the card keeps (--space-4). */
export const INSET = 16;
/** The stem keeps its length when the card is clamped away. */
export const STEM_MIN = 20;
/** Past this the stem is a lead, not a connector (--space-10). */
export const STEM_MAX = 40;
/** A target that reaches within this of both card corners spans the card. */
const EDGE = 28;
/** How far a card slid along its target's edge may stand off the ring and still reach it with its stem. */
const REACH = 28;

export interface Size {
  w: number;
  h: number;
}

export interface Placement {
  left: number;
  top: number;
  side: Side;
  /** The stem's length. */
  stem: number;
  /** Where the stem meets the card's facing edge, from its start corner. */
  at: number;
}

const OPPOSITE: Record<Side, Side> = {
  right: "left",
  left: "right",
  top: "bottom",
  bottom: "top",
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

/**
 * Where the card goes. `side` is where the card sits relative to the target;
 * it flips to the opposite side when the window has no room there, and the
 * card clamps inside the inset either way. `align` runs along the target's
 * edge. The stem lands on the target's centre line, or by the title, mid-card
 * or by the verbs when the target spans the card. `clear` is a region the
 * card must not cover, or several: a card beside its target is pushed past
 * all of them (a pane beside a small target inside it); a card above or
 * below its target slides along the target's edge to the nearest side of a
 * region it covers that leaves it covering none, only as far as its stem
 * still lands on the ring. Where no slide clears everything, it takes the
 * one that covers least, so the person's own notes come out from under it
 * before the agent's wide block does.
 */
export function placeCard(
  rect: Rect,
  side: Side,
  align: Align,
  size: Size,
  vp: Size,
  clear: Rect | readonly Rect[] | null = null,
): Placement {
  const off = RING_GAP + STEM;
  const clears: readonly Rect[] = clear
    ? Array.isArray(clear)
      ? (clear as readonly Rect[])
      : [clear as Rect]
    : [];
  const room: Record<Side, number> = {
    right: vp.w - (rect.left + rect.width) - off - size.w,
    left: rect.left - off - size.w,
    bottom: vp.h - (rect.top + rect.height) - off - size.h,
    top: rect.top - off - size.h,
  };
  let s = side;
  if (room[s] < INSET && room[OPPOSITE[s]] >= INSET) s = OPPOSITE[s];

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  let left: number;
  let top: number;
  if (s === "right" || s === "left") {
    left =
      s === "right" ? rect.left + rect.width + off : rect.left - off - size.w;
    top =
      align === "start"
        ? rect.top - RING_GAP
        : align === "end"
          ? rect.top + rect.height + RING_GAP - size.h
          : cy - size.h / 2;
  } else {
    top =
      s === "bottom" ? rect.top + rect.height + off : rect.top - off - size.h;
    left =
      align === "start"
        ? rect.left - RING_GAP
        : align === "end"
          ? rect.left + rect.width + RING_GAP - size.w
          : cx - size.w / 2;
  }
  if (clears.length && (s === "right" || s === "left")) {
    for (const c of clears) {
      if (s === "right") left = Math.max(left, c.left + c.width + off);
      else left = Math.min(left, c.left - off - size.w);
    }
  }
  if (clears.length && (s === "top" || s === "bottom")) {
    const over = (l: number, c: Rect) =>
      Math.min(l + size.w, c.left + c.width) - Math.max(l, c.left) > 0 &&
      Math.min(top + size.h, c.top + c.height) - Math.max(top, c.top) > 0;
    const covered = (l: number) =>
      clears.reduce((sum, c) => {
        const w = Math.min(l + size.w, c.left + c.width) - Math.max(l, c.left);
        const h =
          Math.min(top + size.h, c.top + c.height) - Math.max(top, c.top);
        return w > 0 && h > 0 ? sum + w * h : sum;
      }, 0);
    const hit = clears.filter((c) => over(left, c));
    if (hit.length) {
      const fits = (l: number) =>
        l >= INSET &&
        l + size.w <= vp.w - INSET &&
        l + REACH <= rect.left + rect.width + RING_GAP &&
        l + size.w - REACH >= rect.left - RING_GAP;
      // Past each region, or as far as the stem reaches when that is
      // nearer than a region's edge allows.
      const slid = [
        ...clears.flatMap((c) => [
          c.left + c.width + off,
          c.left - off - size.w,
        ]),
        rect.left + rect.width + RING_GAP - REACH,
        rect.left - RING_GAP - size.w + REACH,
      ]
        .filter(fits)
        .sort((a, b) => Math.abs(a - left) - Math.abs(b - left));
      const clean = slid.find((l) => covered(l) === 0);
      if (clean !== undefined) left = clean;
      else {
        // None clears all: the one that leaves the fewest regions under
        // the card, then the least area, as long as it comes off at least
        // one; a region too wide to clear is not worth sliding for. Many
        // small regions (the person's notes) outrank one wide block.
        const under = (l: number) => clears.filter((c) => over(l, c)).length;
        const frees = slid.filter((l) => hit.some((c) => !over(l, c)));
        const least = frees.reduce<number | null>(
          (best, l) =>
            best === null ||
            under(l) < under(best) ||
            (under(l) === under(best) && covered(l) < covered(best))
              ? l
              : best,
          null,
        );
        if (least !== null) left = least;
      }
    }
  }
  const cl = clamp(left, INSET, Math.max(INSET, vp.w - INSET - size.w));
  const ct = clamp(top, INSET, Math.max(INSET, vp.h - INSET - size.h));

  // The real distance from the card's edge to the ring when the two are
  // close, a lead of fixed length when the card had to stand off.
  const lead = (d: number) => Math.min(STEM_MAX, Math.max(STEM_MIN, d));
  let stem: number;
  if (s === "right") stem = lead(cl - (rect.left + rect.width + RING_GAP));
  else if (s === "left") stem = lead(rect.left - RING_GAP - (cl + size.w));
  else if (s === "bottom")
    stem = lead(ct - (rect.top + rect.height + RING_GAP));
  else stem = lead(rect.top - RING_GAP - (ct + size.h));

  let at: number;
  if (s === "right" || s === "left") {
    const spans =
      rect.top <= ct + EDGE && rect.top + rect.height >= ct + size.h - EDGE;
    at = spans
      ? align === "start"
        ? EDGE
        : align === "end"
          ? size.h - EDGE
          : size.h / 2
      : clamp(cy - ct, EDGE, Math.max(EDGE, size.h - EDGE));
  } else {
    const spans =
      rect.left <= cl + EDGE && rect.left + rect.width >= cl + size.w - EDGE;
    at = spans
      ? align === "start"
        ? EDGE
        : align === "end"
          ? size.w - EDGE
          : size.w / 2
      : clamp(cx - cl, EDGE, Math.max(EDGE, size.w - EDGE));
  }
  return { left: cl, top: ct, side: s, stem, at };
}
